/**
 * Home-video audiences and Intent tags, shared by the video features.
 * Names match the steward-api Swagger docs (components.schemas.HomeVideoAudience,
 * IntentTag and HomeVideoSlot) exactly.
 */

/**
 * Which home page a slot or playlist belongs to.
 *
 * @typedef {'educator'|'partner'|'both'} HomeVideoAudience
 */

/**
 * An Intent tag on a video. Requests send only the ids.
 *
 * @typedef {Object} IntentTag
 * @property {number} id - tag.id of an Intent tag
 * @property {'Educator'|'Partner'} name
 */

/**
 * A home-page slot holding a video. Set only via PUT /staff/videos/home-order.
 *
 * @typedef {Object} HomeVideoSlot
 * @property {HomeVideoAudience} audience
 * @property {number} position - 1 to 5; 1 is the featured card
 */

export const HomeVideoAudiences = Object.freeze({
  Educator: 'educator',
  Partner: 'partner',
  Both: 'both'
});

/** In display order. */
export const HOME_VIDEO_AUDIENCES = Object.freeze([
  HomeVideoAudiences.Educator,
  HomeVideoAudiences.Partner,
  HomeVideoAudiences.Both
]);

export const IntentTagNames = Object.freeze({
  Educator: 'Educator',
  Partner: 'Partner'
});

/** In display order. */
export const INTENT_TAG_NAMES = Object.freeze([IntentTagNames.Educator, IntentTagNames.Partner]);

/** The Intent tag names that qualify a video for each audience. */
const ELIGIBLE_TAGS = Object.freeze({
  [HomeVideoAudiences.Educator]: [IntentTagNames.Educator],
  [HomeVideoAudiences.Partner]: [IntentTagNames.Partner],
  [HomeVideoAudiences.Both]: [IntentTagNames.Educator, IntentTagNames.Partner]
});

/**
 * @param {HomeVideoAudience} audience
 * @returns {string[]} the Intent tag names that qualify a video for it
 */
export function eligibleTagNames(audience) {
  return ELIGIBLE_TAGS[audience] || [];
}

/**
 * @param {{ audiences?: IntentTag[] }|null|undefined} video
 * @returns {string[]} the video's Intent tag names
 */
export function videoTagNames(video) {
  const tags = Array.isArray(video?.audiences) ? video.audiences : [];
  return tags.map((tag) => tag?.name).filter(Boolean);
}

/**
 * Whether a video can be pinned to an audience's home page: educator needs
 * an Educator tag, partner a Partner tag, both either one.
 *
 * @param {{ audiences?: IntentTag[] }|null|undefined} video
 * @param {HomeVideoAudience} audience
 * @returns {boolean}
 */
export function isEligible(video, audience) {
  const names = videoTagNames(video);
  return eligibleTagNames(audience).some((name) => names.includes(name));
}
